import React, {
  RefObject,
  useMemo,
  useState,
  useCallback,
  SyntheticEvent,
} from 'react';
import clsx from 'clsx';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import values from 'lodash/values';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import IconButton from '@mui/material/IconButton';
import {
  isEmptyString,
  notEmptyString,
} from 'utils/misc';
import ToolTip from 'core/components/Tooltip';
import PinIcon from 'core/icons/pin';
import AutoCompletedInput from 'core/components/AutoComplete';
import Spinner from 'components/Spinner';
import Wrapper from 'components/Input/Wrapper';
import Input from 'components/Input';
import {
  useInsuranceSearch,
  useGetFavoriteInsurances,
} from '@/components/hooks/insurances';
import {
  getAddressString,
} from 'utils/string';
import LabelWithActions from 'components/Label/LabelWithActions';
import {
  InsuranceSearchType,
} from '@/dtos';
import {
  filterInsurances,
  Payer,
} from '@/utils/helper';

type InsuranceOptionProps = {
  option: Payer;
  props: Omit<Record<string, any>, 'key'>;
  optionKey?: React.Key;
  displayPin?: boolean;
};

function InsuranceOption({
  option,
  props,
  optionKey,
  displayPin = true,
}: InsuranceOptionProps) {
  const keyName = optionKey?.toString() ?? option?.payerEntryId?.toString() ?? '';
  return (
    <div
      {...props}
      key={keyName}
      className={clsx(
        props?.className,
        'flex flex-row !items-center !w-full gap-3',
      )}
    >
      {option.isFavorited && displayPin && (
        <span>
          <PinIcon
            className="!text-base !text-blue-500"
          />
        </span>
      )}
      <div key={`payer-${keyName}`} className="flex flex-col !items-start !justify-start !w-full !text-left gap-2">
        <span className="font-semibold">
          {option?.payerName ?? '--'}
        </span>
        <span className="flex gap-2 populate-vertical-list-text">
          <span className="populate-list-item">
            {compact([option.payerId, option.isPaper ? ' (Paper)' : null]).join(' ') || '--'}
          </span>
        </span>
        <span className="populate-list-item">{option.insuranceType}</span>
        {option.address && (
          <span>
            {getAddressString(option.address)}
          </span>
        )}
      </div>
    </div>
  );
}

type Props = {
  className?: string;
  id: string;
  value?: string | null;
  label?: string | null;
  placeholder?: string | null;
  readonly?: boolean;
  onChange: (value: Payer) => void;
  inputRef?: RefObject<HTMLInputElement>;
  resetOnEmpty?: boolean;
  required?: boolean;
  error?: boolean;
  filterOptions?: (options: Payer[]) => Payer[];
  labelClasses?: string;
  selectOnFocus?: boolean;
  multiple?: boolean;
  renderTags?: () => null;
  clearOnBlur?: boolean;
  searchType?: InsuranceSearchType;
  toggleFavorites?: boolean;
  favoriteOnly?: boolean;
};

function PayerID({ value }: { value?: Payer | string | null }) {
  return (
    <ToolTip title="Payer ID">
      <span className="font-semibold text-xs cursor-pointer">
        {(value as Payer)?.payerId}
      </span>
    </ToolTip>
  );
}

export default function InsuranceLookup({
  className,
  id,
  onChange,
  placeholder = 'Enter Payer Name, Number, or Address...',
  value: _value,
  readonly,
  label = 'Payer',
  inputRef,
  required = false,
  error = false,
  filterOptions = (x) => x,
  labelClasses = 'font-semibold text-xs text-gray-500',
  selectOnFocus = false,
  multiple = false,
  renderTags,
  clearOnBlur = false,
  searchType = 'All',
  toggleFavorites = true,
  favoriteOnly = true,
}: Props) {
  const [inputValue, setInputValue] = useState<string>(multiple ? '' : _value ?? '');
  const [open, setOpen] = useState<boolean>(false);
  const value = multiple || typeof _value !== 'string'
    ? _value
    : { payerName: _value ?? '' } as Payer;
  const [showFavorites, setShowFavorites] = useState(favoriteOnly);

  const classes = clsx('flex flex-col flex-auto', {
    '!space-y-0': isEmptyString(label),
    'space-y-1': !isEmptyString(label),
  });
  const containerClasses = clsx('flex flex-wrap w-full gap-4 mb-3', className);

  const { data: _options = [], isLoading } = useInsuranceSearch(
    inputValue,
    searchType,
    open && !isEmptyString(inputValue),
  );

  const { data: _favorites = [] } = useGetFavoriteInsurances();

  const options = showFavorites ? _favorites : _options;

  const handleChange = (value: Payer | Payer[]) => {
    setOpen(false);
    onChange(value as Payer);
  };

  const handleInputChange = useCallback(debounce((event: SyntheticEvent, newInputValue: string) => {
    if (open) {
      setInputValue(newInputValue);
    }
  }, 700), [open]);

  const noOptionsText = useMemo(() => (
    options.length === 0 && inputValue === ''
      ? 'Start typing to see the list'
      : 'No Insurance Found'
  ), [options, inputValue]);

  const isEmpty = useMemo(() => (
    isNil(value) || values(value).every(isEmptyString)
  ), [value]);

  const handleToggleFavoritesList = () => {
    setShowFavorites((prev) => !prev);
    inputRef?.current?.focus();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const favoritesPinTooltipTitle = useMemo(() => {
    if (_favorites?.length > 0) {
      return 'Show pinned insurances';
    }
    return 'No pinned insurances are available';
  }, [_favorites]);

  return (
    <div className={containerClasses}>
      <Wrapper
        id="patient-select"
        className={classes}
        required={required}
        error={error}
        showErrorText={false}
        labelClasses={labelClasses}
        label={!isNil(label) && (
          <LabelWithActions
            required={required}
            containerClassName="!items-end"
            id={id}
            label={label}
            actions={!readonly && toggleFavorites && (
              <ToolTip title={favoritesPinTooltipTitle}>
                <span>
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    color="info"
                    className="is-favorite disabled:opacity-50"
                    disabled={_favorites?.length === 0}
                    onClick={handleToggleFavoritesList}
                  >
                    <PinIcon
                      className={clsx('!text-sm text-gray-500', {
                        '!text-blue-500': showFavorites,
                      })}
                    />
                  </IconButton>
                </span>
              </ToolTip>
            )}
          />
        )}
      >
        {readonly
          ? (
            <Input
              id={id}
              onChange={noop}
              className="flex flex-1 space-y-1"
              inputProps={{ readOnly: true }}
              value={notEmptyString((value as Payer)?.payerName) ? (value as Payer)?.payerName : ''}
              endIcon={<PayerID value={value} />}
            />
          ) : (
            <AutoCompletedInput<Payer>
              value={multiple ? (value ?? []) : (value ?? null)}
              isOptionEqualToValue={(option, selectedValue) => (
                typeof selectedValue !== 'string' && option.payerName === selectedValue.payerName
              )}
              id={id}
              disabled={readonly}
              options={options}
              onOpen={() => { setOpen(true); }}
              onClose={handleClose}
              open={open}
              multiple={multiple}
              getLabel={(option) => option?.payerName ?? ''}
              renderTags={renderTags}
              renderOption={(props: Record<string, any>, option: Payer) => {
                const {
                  key,
                  ...optionProps
                } = props;

                return (
                  <InsuranceOption
                    key={key ?? option.payerEntryId}
                    optionKey={key ?? option.payerEntryId}
                    props={optionProps}
                    option={option}
                    displayPin={false}
                  />
                );
              }}
              loading={isLoading}
              inputRef={inputRef}
              openOnFocus
              includeInputInList
              className="w-full"
              onChange={handleChange}
              filterOptions={showFavorites ? filterInsurances : filterOptions}
              placeholder={placeholder ?? ''}
              onInputChange={handleInputChange}
              classes={{ inputRoot: '!py-[7px]' }}
              inputProps={{
                className: 'placeholder:text-sm md:placeholder:text-xs',
              }}
              clearOnBlur={clearOnBlur}
              selectOnFocus={selectOnFocus}
              blurOnSelect={false}
              noOptionsText={noOptionsText}
              dropdownClassName={clsx('!overflow-hidden !w-80', {
                '!p-0': options?.length && isEmpty && !isLoading,
                'p-2': options?.length === 0,
              })}
              popupIcon={isLoading ? <Spinner /> : undefined}
              InputProps={{
                classes: { root: '!pr-2' },
                endAdornment: <PayerID value={value} />,
              }}
            />
          )}
      </Wrapper>
    </div>
  );
}
